import { useCallback, useMemo } from "react";
import { getNextStep, reconstructSteps } from "../../utils/reportWorkflow";
import { DONE } from "../../constants/reportStep";
import ReportStep from "../../models/pageModels/ReportStep";
import { useReportBuilderStore } from "../../store/report/useReportBuilderStore";

const normalize = (val: any) =>
  val === null || val === undefined ? "" : String(val);

export function useReportBuilder() {
  const reportType = useReportBuilderStore((s) => s.reportType);
  const steps = useReportBuilderStore((s) => s.steps);
  const showRunReportOptions = useReportBuilderStore((s) => s.showRunReportOptions);
  const initialParameters = useReportBuilderStore((s) => s.initialParameters);

  const setReportType = useReportBuilderStore((s) => s.setReportType);
  const setSteps = useReportBuilderStore((s) => s.setSteps);
  const setShowRunReportOptions = useReportBuilderStore((s) => s.setShowRunReportOptions);
  const setInitialParameters = useReportBuilderStore((s) => s.setInitialParameters);
  const resetBuilder = useReportBuilderStore((s) => s.resetBuilder);

  const findStep = (currentSteps: ReportStep[], stepName: string) =>
    currentSteps.find((s) => s.step === stepName);

  const updateReportType = useCallback((value: string) => {
    setReportType(value);
    setSteps([]);
    setShowRunReportOptions(false);
    setInitialParameters({});
  }, [setReportType, setSteps, setShowRunReportOptions, setInitialParameters]);

  const startWorkflow = useCallback(() => {
    if (!reportType) return;
    const nextStep = getNextStep(reportType);
    if (nextStep) {
      setSteps([{
        step: nextStep.step,
        values: [],
        routeByValues: nextStep.routeByValues,
        expanded: true,
        disabled: false,
        validation: [],
      }]);
    }
  }, [reportType, setSteps]);

  const getAllStepValues = useCallback(() => {
    const allValues: Record<string, { name: string; value: string }[]> = {};
    steps.forEach((s) => (allValues[s.step] = s.values));
    return allValues;
  }, [steps]);

  const handleOnStepExpanded = useCallback((stepName: string, expanded: boolean) => {
    setSteps((currentSteps) => {
      const newSteps = [...currentSteps];
      const matchingStep = findStep(newSteps, stepName);
      if (matchingStep) {
        const index = newSteps.indexOf(matchingStep);
        newSteps[index] = { ...matchingStep, expanded };
      }
      return newSteps;
    });
  }, [setSteps]);

  const handleNextStep = useCallback((stepName: string) => {
    setSteps((currentSteps) => {
      const newSteps = [...currentSteps];
      const matchingStep = findStep(newSteps, stepName);
      if (!matchingStep) return currentSteps;

      const index = newSteps.indexOf(matchingStep);
      newSteps[index] = { ...matchingStep, expanded: false };

      const locator = `${reportType}/${newSteps
        .map((s) =>
          s.routeByValues
            ? `${s.step}[${s.values.map((v) => v.value).join(",")}]`
            : s.step,
        )
        .join("/")}`;

      const nextStep = getNextStep(locator);

      if (!nextStep) {
        if (newSteps.length > index + 1) newSteps.splice(index + 1);
      } else if (nextStep.step === DONE) {
        setShowRunReportOptions(true);
      } else {
        setShowRunReportOptions(false);
        const existingNextStep = newSteps.find((s) => s.step === nextStep.step);
        if (existingNextStep) {
          const nextIndex = newSteps.indexOf(existingNextStep);
          newSteps[nextIndex] = { ...existingNextStep, expanded: true };
        } else {
          newSteps.push({
            step: nextStep.step,
            values: [],
            routeByValues: nextStep.routeByValues,
            expanded: true,
            disabled: false,
            validation: [],
          });
        }
      }
      return newSteps;
    });
  }, [reportType, setSteps, setShowRunReportOptions]);

  const updateStepValues = useCallback((data: { step: string; values: { name: string; value: string }[] }) => {
    setSteps((currentSteps) => {
      const newSteps = [...currentSteps];
      const matchingStep = findStep(newSteps, data.step);
      if (!matchingStep) return currentSteps;

      setShowRunReportOptions(false);

      const index = newSteps.indexOf(matchingStep);
      const updatedStep = { ...matchingStep, values: data.values };

      const validationValue = data.values.find((v) => v.name === "validation");
      if (validationValue?.value) {
        updatedStep.validation = JSON.parse(validationValue.value);
        updatedStep.values = updatedStep.values.filter((v) => v.name !== "validation");
      }

      newSteps[index] = updatedStep;

      if (newSteps.length > index + 1) {
        newSteps.splice(index + 1);
        setShowRunReportOptions(false);
      }

      return newSteps;
    });
  }, [setSteps, setShowRunReportOptions]);

  const loadWorkflow = useCallback((type: string, parameters: Record<string, string>) => {
    const reconstructed = reconstructSteps(type, parameters);
    setReportType(type);
    setSteps(reconstructed);
    setShowRunReportOptions(false);
    setInitialParameters(parameters);
  }, [setReportType, setSteps, setShowRunReportOptions, setInitialParameters]);

  const getFlattenedParameters = useCallback(() => {
    const parameters: Record<string, string> = {};
    steps.forEach((step) =>
      step.values.forEach((v) => {
        if (v.name !== "validation") {
          parameters[v.name] = v.value;
        }
      }),
    );
    return parameters;
  }, [steps]);

  const isDirty = useMemo(() => {
    const currentParams = getFlattenedParameters();
    const ignoredKeys = ["validation", "reportId"];

    const keys1 = Object.keys(currentParams).filter((k) => !ignoredKeys.includes(k));
    const keys2 = Object.keys(initialParameters).filter((k) => !ignoredKeys.includes(k));

    for (const k of keys1) {
      if (normalize(currentParams[k]) !== normalize(initialParameters[k])) return true;
    }
    for (const k of keys2) {
      if (!(k in currentParams) && normalize(initialParameters[k]) !== "") return true;
    }

    return false;
  }, [getFlattenedParameters, initialParameters]);

  return {
    reportType,
    steps,
    showRunReportOptions,
    isDirty,
    updateReportType,
    startWorkflow,
    handleOnStepExpanded,
    handleNextStep,
    updateStepValues,
    getAllStepValues,
    loadWorkflow,
    resetWorkflow: resetBuilder,
    getFlattenedParameters,
    setInitialParameters,
  };
}